import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, Coins, Zap, Award, ShoppingBag, Star, CheckCircle2, X, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { notificationAPI } from '../lib/api';
import { toast } from 'sonner';

const NOTIF_ICONS = {
  coins: Coins,
  xp: Zap,
  achievement: Award,
  purchase: ShoppingBag,
  level_up: Star,
  activity: CheckCircle2,
  activity_approval: CheckCircle2,
  activity_rejected: XCircle,
  info: Bell,
};

const NOTIF_COLORS = {
  coins: 'text-amber-500 bg-amber-50',
  xp: 'text-purple-500 bg-purple-50',
  achievement: 'text-yellow-500 bg-yellow-50',
  purchase: 'text-blue-500 bg-blue-50',
  level_up: 'text-emerald-500 bg-emerald-50',
  activity: 'text-green-500 bg-green-50',
  activity_approval: 'text-orange-500 bg-orange-50',
  activity_rejected: 'text-red-500 bg-red-50',
  info: 'text-slate-500 bg-slate-50',
};

export default function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data || []);
    } catch {}
  }, []);

  // WebSocket connection for real-time push
  useEffect(() => {
    if (!user?.token) return;

    const connectWS = () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws/notifications?token=${user.token}`);
      
      ws.onopen = () => {
        wsRef.current = ws;
        // Send ping every 30s to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && data.notification) {
            const notif = data.notification;
            // Add to state
            setNotifications(prev => [notif, ...prev]);
            // Show toast
            const Icon = NOTIF_ICONS[notif.type] || Bell;
            toast(notif.title, {
              description: notif.message,
              icon: <Icon className="w-4 h-4" />,
              duration: 5000,
            });
          }
        } catch {}
      };
      
      ws.onclose = () => {
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        wsRef.current = null;
        // Reconnect after 5s
        reconnectTimer.current = setTimeout(connectWS, 5000);
      };
      
      ws.onerror = () => ws.close();
    };

    connectWS();
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [user?.token]);

  // Initial load + polling fallback every 30s
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch {}
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-heading font-semibold text-sm text-slate-700">Notificaciones</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600" onClick={handleMarkAllRead}>
                  <Check className="w-3 h-3 mr-1" />Marcar todo
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifications.slice(0, 30).map(notif => {
                const Icon = NOTIF_ICONS[notif.type] || Bell;
                const colorClass = NOTIF_COLORS[notif.type] || NOTIF_COLORS.info;
                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 p-3 border-b border-slate-50 cursor-pointer transition-colors ${
                      notif.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/40 hover:bg-blue-50/60'
                    }`}
                    onClick={() => !notif.read && handleMarkRead(notif.id)}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${notif.read ? 'text-slate-600' : 'text-slate-900'}`}>
                          {notif.title}
                        </p>
                        {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                      </div>
                      <p className={`text-xs text-slate-500 ${notif.type === 'activity_rejected' ? 'line-clamp-3' : 'truncate'}`}>{notif.message}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(notif.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
